import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { SxProps, Theme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useBodyClass from '../../../hooks/useBodyClass';
import ZapMobileHeader from '../../../components/mobile/ZapMobileHeader';
import zapIconActive from '../../../assets/images/zapIconActive.svg';
import StorefrontImage from '../../../assets/images/storefront.svg';
import LinkinBioImage from '../../../assets/images/link-in-bio.svg';
import { SocialIconStack, SocialPlatform } from '../../../components/social-icon-stack/social-icon-stack';
import WhatIsZapModal from './WhatIsZapModal';
import { SubscriptionService } from '../../../core/services';
import * as gtUtil from '../../../core/utility';

const subscriptionService = new SubscriptionService();

interface HubCard {
    title: string;
    subtitle: string;
    description: string;
    route: string;
    icon: React.ReactNode;
    // Social platforms the tool works with, shown beside the subtitle.
    platforms: SocialPlatform[];
    // Pill after the icons for tools that aren't tied to specific platforms.
    platformsLabel?: string;
    // Entitlement key gating this card; undefined = always available.
    entitlementKey?: 'storefront_enabled' | 'link_in_bio_enabled';
    // Section id inside WhatIsZapModal this card's info icon jumps to.
    introSection: string;
}


// The circular gradient lightning mark used for the ZAP Story icon — also used
// as the page's top logo per design request.
const ZapStoryIcon: React.FC<{ size?: number }> = ({ size = 56 }) => (
    <Box sx={{
        width: size, height: size, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: '3px', flexShrink: 0,
    }}>
        <Box sx={{ width: '100%', height: '100%', borderRadius: '50%', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size * 0.42} height={size * 0.42} viewBox="0 0 24 24" fill="none">
                <defs>
                    <linearGradient id="zapStoryBolt" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#833ab4" />
                        <stop offset="55%" stopColor="#fd1d84" />
                        <stop offset="100%" stopColor="#fcb045" />
                    </linearGradient>
                </defs>
                <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" fill="url(#zapStoryBolt)" />
            </svg>
        </Box>
    </Box>
);

// Flat rounded-square gradient icon container used for the feature cards.
const CardIcon: React.FC<{ children: React.ReactNode; sx?: SxProps<Theme> }> = ({ children, sx }) => (
    <Box sx={{
        width: 60, height: 60, borderRadius: '16px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        ...sx
    }}>
        {children}
    </Box>
);

const AutomationIcon = () => (
    <CardIcon sx={{border: '1px solid #ececf0', bgcolor: '#fff'}}>
        <img src={zapIconActive} alt="Zap Logo" width={28} height={38} />
    </CardIcon>
);

const StorefrontIcon = () => (
    <CardIcon sx={{border: '1px solid #ececf0', bgcolor: '#fff'}}>
        <img src={StorefrontImage} alt="Storefront Logo" width={40} height={40} />
    </CardIcon>
);

const BioLinkIcon = () => (
    <CardIcon sx={{border: '1px solid #ececf0', bgcolor: '#fff'}}>
        <img src={LinkinBioImage} alt="LinkinBio Logo" width={40} height={40} />
    </CardIcon>
);

const CARDS: HubCard[] = [
    {
        title: 'Zap Automations',
        subtitle: 'Instagram DM',
        description: 'Auto-reply DMs when your followers comment with specific keywords on your posts.',
        route: '/influencer/zap/automations',
        icon: <AutomationIcon />,
        platforms: ['instagram'],
        introSection: 'automations',
    },
    {
        title: 'Zap Storefront',
        subtitle: 'Your Shop',
        description: 'Sell products directly from your profile with a personalized creator storefront.',
        route: '/influencer/storefront',
        icon: <StorefrontIcon />,
        // platforms: ['instagram', 'facebook', 'whatsapp', 'youtube', 'pinterest'],
        platforms: ['instagram', 'facebook', 'youtube'],
        platformsLabel: '+ Anywhere',
        introSection: 'storefront',
        entitlementKey: 'storefront_enabled',
    },
    {
        title: 'Zap - Link in Bio',
        subtitle: 'Bio Page',
        description: 'Build a beautiful link page that showcases all your content and social profiles.',
        route: '/influencer/link-in-bio',
        icon: <BioLinkIcon />,
        // platforms: ['instagram', 'youtube', 'tiktok', 'x', 'threads'],
        platforms: ['instagram', 'facebook', 'youtube'],
        platformsLabel: '+ Anywhere',
        introSection: 'link-in-bio',
        entitlementKey: 'link_in_bio_enabled',
    },
];

export const ZapHub: React.FC = () => {
    const navigate = useNavigate();
    useBodyClass('zap-hub-page');

    const user = useSelector((state: any) => state.UserReducer.UserData);
    // Current plan entitlements — gate Storefront / Link-in-Bio the same way the
    // Zap Automations flow gates features by the active plan's entitlements.
    const [entitlements, setEntitlements] = useState<any>(null);
    // "What is Zap?" explainer. `introSection` deep-links the modal to one tool's
    // section when it is opened from that card's info icon.
    const [introOpen, setIntroOpen] = useState(false);
    const [introSection, setIntroSection] = useState<string | undefined>(undefined);

    const openIntro = (sectionId?: string) => {
        setIntroSection(sectionId);
        setIntroOpen(true);
    };

    useEffect(() => {
        let cancelled = false;
        const fetchEntitlements = async () => {
            if (!user?.influencer_id) return;
            try {
                const res: any = await subscriptionService.getCurrentPlan({
                    account_id: user.account_id,
                    influencer_id: user.influencer_id,
                    user_id: user.user_id,
                    brand_id: 0,
                    user_type: 'Influencer',
                });
                if (!cancelled && res?.status === gtUtil.Constants.SUCCESS) {
                    setEntitlements(res.data?.entitlements ?? {});
                }
            } catch {
                if (!cancelled) setEntitlements({});
            }
        };
        fetchEntitlements();
        return () => { cancelled = true; };
    }, [user?.influencer_id, user?.account_id, user?.user_id]);

    const isLocked = (card: HubCard): boolean => {
        if (!card.entitlementKey) return false;      // always-available (Automations)
        if (entitlements === null) return false;     // not loaded yet — don't flash a lock
        return !entitlements?.[card.entitlementKey];
    };

    const handleCardClick = (card: HubCard) => {
        if (isLocked(card)) {
            // Not on the plan → send them to upgrade, mirroring the automations gate.
            navigate('/influencer/settings/subscription');
            return;
        }
        navigate(card.route);
    };

    return (
        <Box sx={{
            minHeight: "calc(100vh - 70px)",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            pt: { xs: 4, sm: 6, lg: 0 },
            pb: 10,
        }}>
            {/* <ZapMobileHeader title="Zap" showBack={false} /> */}
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Box sx={{ mx: 'auto', mb: 2, width: '60px', height: '60px', borderRadius:'50%', border:'1px solid #ececf0', p: 1.5, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {/* <ZapStoryIcon /> */}
                    <img src={zapIconActive} alt="Zap Logo" width={22} height={22} />
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.4px', color: '#111' }}>
                    Zap
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
                    Supercharge your creator presence
                </Typography>
                {/* Mirrors the "How it works? / How to use Zap" helpers on the
                    Automations screen — the hub needed its own explainer. */}
                <Box
                    onClick={() => openIntro()}
                    role="button"
                    aria-label="What is Zap?"
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1.25,
                        cursor: 'pointer', borderRadius: '999px', px: 1.5, py: 0.5,
                        border: '1px solid #e8d3ec', bgcolor: '#faf5fb', color: '#9C52A2',
                        fontSize: '0.8125rem', fontWeight: 600,
                        transition: 'background-color .15s ease',
                        '&:hover': { bgcolor: '#f3e6f5' },
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} /> What is Zap?
                </Box>
            </Box>

            {/* Cards */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, maxWidth: 1200, width: '100%', mx: 'auto' }}>
                {CARDS.map((card) => {
                    const locked = isLocked(card);
                    return (
                    <Box
                        key={card.route}
                        onClick={() => handleCardClick(card)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2.5,
                            bgcolor: '#fff',
                            border: '1px solid #ececf0',
                            borderRadius: '18px',
                            px: 2,
                            py: 3,
                            cursor: 'pointer',
                            opacity: locked ? 0.85 : 1,
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                            '&:hover': {
                                borderColor: '#d8d8e0',
                                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                            },
                        }}
                    >
                        {/* Per-card explainer — opens the modal at this tool's section
                            without triggering the card's navigation. */}
                        <Box
                            onClick={(e) => { e.stopPropagation(); openIntro(card.introSection); }}
                            role="button"
                            aria-label={`What is ${card.title}?`}
                            title={`What is ${card.title}?`}
                            sx={{
                                position: 'absolute', top: 10, right: 10, lineHeight: 0,
                                color: '#b8b8c2', cursor: 'pointer',
                                '&:hover': { color: '#9C52A2' },
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        {card.icon}
                        <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ color: '#111', lineHeight: 1, fontSize: '1.05rem', fontWeight: 700 }}>
                                    {card.title}
                                </Typography>
                                {locked && (
                                    <Chip
                                        icon={<LockOutlinedIcon sx={{ fontSize: 13 }} />}
                                        label="Upgrade"
                                        size="small"
                                        sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: '#f3f0ff', color: '#7c3aed', '& .MuiChip-icon': { color: '#7c3aed' } }}
                                    />
                                )}
                            </Box>
                            {/* A single platform reads as a label ("[IG] Instagram DM");
                                several read as a trailing "works with" stack. */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                                {/* {card.platforms.length === 1 && <SocialIconStack platforms={card.platforms} size={22} />} */}
                                <Typography sx={{ color: '#9ca3af', fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.2 }}>
                                    {card.subtitle}
                                </Typography>
                                {/* {card.platforms.length > 1 && <SocialIconStack platforms={card.platforms} size={24} />} */}
                                {/* Subtitle first, platform icons after — same order on every card. */}
                                <SocialIconStack platforms={card.platforms} extraLabel={card.platformsLabel} size={24} />
                            </Box>
                            <Typography sx={{ color: '#6b7280', lineHeight: 1.5, fontSize: '0.875rem', mt: 0.75 }}>
                                {card.description}
                            </Typography>
                        </Box>
                    </Box>
                    );
                })}
            </Box>

            <WhatIsZapModal
                open={introOpen}
                onClose={() => setIntroOpen(false)}
                initialSection={introSection}
            />
        </Box>
    );
};
